import clsx from 'clsx';
import { forwardRef, type PropsWithChildren } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Grid } from '@dynatrace/strato-components/layouts';

import * as styles from './InputGroup.sty.js';

/**
 * Props for the InputGroup component.
 * @public
 */
export interface InputGroupProps
  extends PropsWithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {}

/**
 * The `InputGroup` component is the outermost layout component of a field
 * component (for example `TextInput`), providing support for grouping.
 * @public
 */
export const InputGroup = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  InputGroupProps
>((inputGroupProps, ref) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = inputGroupProps;

  return (
    <Grid
      ref={ref}
      gridAutoFlow="column"
      gridAutoRows="max-content"
      justifyContent="start"
      columnGap={2}
      rowGap={4}
      className={clsx(consumerClassName, styles.inputGroup)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingProps}
    >
      {children}
    </Grid>
  );
});

(InputGroup as typeof InputGroup & { displayName: string }).displayName =
  'InputGroup';
